import { isValidElement, type ReactNode } from "react";
import type { Components } from "react-markdown";

// The design-language mapping for Markdown output, shared by every renderer: the assistant's
// replies (AgentMarkdown) and the Markdown a person writes into a shared item (MarkdownText).
// Tailwind's preflight resets list markers and heading sizes, so each element is mapped back
// explicitly rather than pulling in @tailwindcss/typography. Raw HTML is never enabled —
// react-markdown escapes it by default.

/** An inline link in Markdown prose: the ink underline, with the ring every hand-rolled interactive element carries. */
export const MARKDOWN_LINK_CLASS =
  "rounded text-ink underline decoration-ink/25 underline-offset-2 transition hover:text-ink-accent hover:decoration-ink-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui";

/**
 * Slug for a heading, so a generated table of contents can point at it.
 * react-markdown emits no ids of its own, so `[Audience](#audience)` would
 * otherwise be a link to nothing. Mirrors the GitHub rule the generators use:
 * lower case, non-word characters dropped, spaces to dashes.
 */
function headingId(children: ReactNode): string | undefined {
  const text = textOf(children).trim().toLowerCase();
  if (!text) return undefined;
  return text.replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
}

export function textOf(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return "";
}

export const baseComponents: Components = {
  // NOTE: `p`, `li` and `blockquote` are NOT here. Each renderer owns them: AgentMarkdown makes
  // them company-aware (a company named as its own entry replaces that entry, a quoted one must
  // not), MarkdownText keeps a person's line breaks. A plain copy here would be dead and would
  // read as the one in force.
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-0.5 pl-5 last:mb-0 marker:text-ink-muted">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-0.5 pl-5 last:mb-0 marker:text-ink-muted">{children}</ol>
  ),

  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  code: ({ children }) => (
    <code className="rounded bg-ink/10 px-1 py-0.5 text-smycles-meta">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-ink/5 p-2 text-smycles-meta last:mb-0 [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  h1: ({ children }) => (
    <h3 id={headingId(children)} className="mb-1 mt-2 font-semibold first:mt-0 scroll-mt-2">
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h3 id={headingId(children)} className="mb-1 mt-2 font-semibold first:mt-0 scroll-mt-2">
      {children}
    </h3>
  ),
  h3: ({ children }) => (
    <h3 id={headingId(children)} className="mb-1 mt-2 font-semibold first:mt-0 scroll-mt-2">
      {children}
    </h3>
  ),
  hr: () => <hr className="my-2 border-ink/10" />,
  // GFM tables (the multi-person comparison): preflight strips table chrome, so map it back.
  // Wrapped so a wide table scrolls horizontally instead of widening its container.
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left text-smycles-meta">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-ink/15 px-2 py-1 font-semibold whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => <td className="border-t border-ink/10 px-2 py-1 align-top">{children}</td>,
};

